import type {
  PurgaLeadsRequest,
  RecalculoMasivoRequest,
  UpdateLeadStatusRequest,
} from '@/types/crm';
import type { EstadoLead } from '@/types/domain';
import {
  PURGA_DIAS_DEFAULT,
  PURGA_DIAS_MAX,
  PURGA_LIMITE_DEFAULT,
  PURGA_LIMITE_MAX,
  RECALCULO_LIMIT_DEFAULT,
  RECALCULO_LIMIT_MAX,
} from './crm-format';

/**
 * Formulario → cuerpo del request (mismo reparto que en Órdenes: la validación en
 * `crm-validation.ts` y el body acá).
 *
 * Los dos cuerpos del contrato son cortos, pero tienen reglas que se pierden si se arman en el
 * `onSubmit` del diálogo: el `motivo` solo viaja si se escribió (el backend guarda su propio texto
 * por defecto y el historial muestra ese) y el tope del recálculo es un texto que hay que dejar
 * dentro de los límites del backend (1-2000, default 500).
 */

/** Formulario de reclasificación manual del lead. */
export interface LeadStatusFormState {
  /** `''` = todavía sin elegir (el `Select` es el que obliga a escoger). */
  estadoLead: EstadoLead | '';
  /** Motivo (texto libre, hasta 300 caracteres); vacío = el backend guarda el suyo. */
  motivo: string;
}

export function emptyStatusForm(): LeadStatusFormState {
  return { estadoLead: '', motivo: '' };
}

/** Formulario del recálculo masivo (solo ADMIN). */
export interface RecalculoFormState {
  /** Tope de conversaciones a revisar, como texto (el `<input>` siempre es texto). */
  limite: string;
  /** Recalcular también los leads que el equipo reclasificó a mano. */
  incluirManuales: boolean;
}

export function emptyRecalculoForm(): RecalculoFormState {
  return { limite: String(RECALCULO_LIMIT_DEFAULT), incluirManuales: false };
}

/**
 * Campos a los que `formErrorsFromApiError` puede atribuir un mensaje del backend.
 *
 * El schema del `PATCH` solo mira `estadoLead` y `motivo`; los del recálculo son los del body
 * anidado (`conversationIds`, `filtros`, `limite`, `incluirManuales`).
 */
export const LEAD_STATUS_FORM_FIELDS = ['estadoLead', 'motivo'] as const;
export const RECALCULO_FORM_FIELDS = [
  'conversationIds',
  'filtros',
  'limite',
  'incluirManuales',
] as const;

/**
 * El tope del recálculo, ya como número dentro de lo que acepta el backend (1-2000).
 *
 * Un texto ilegible NO rompe la petición larga: cae al default del backend (500), que es lo que el
 * formulario ya mostraba antes de que el admin lo tocara.
 */
export function parseRecalculoLimite(value: string): number {
  const texto = value.trim();
  // Vacío = el admin borró el campo (la validación lo marca antes de llegar acá): se usa el default
  // del backend, que es lo que el formulario mostraba al abrirse.
  if (texto.length === 0) return RECALCULO_LIMIT_DEFAULT;

  const parsed = Number(texto.replace(',', '.'));
  if (!Number.isFinite(parsed)) return RECALCULO_LIMIT_DEFAULT;

  return Math.min(Math.max(Math.trunc(parsed), 1), RECALCULO_LIMIT_MAX);
}

/** Body del `PATCH .../crm/leads/:conversationId/estado`. */
export function updateLeadStatusPayload(
  estadoLead: EstadoLead,
  motivo: string,
): UpdateLeadStatusRequest {
  const texto = motivo.trim();
  return { estadoLead, ...(texto.length > 0 ? { motivo: texto } : {}) };
}

/**
 * Body del `POST .../crm/recalcular`.
 *
 * `conversationIds` y `filtros` no viajan: la pantalla recalcula con el default del backend (las
 * conversaciones clasificadas automáticamente, de más reciente a más vieja). El admin se queda con
 * el tope y con la decisión de pisar o no las reclasificaciones a mano.
 */
export function recalcularMasivoPayload(form: RecalculoFormState): RecalculoMasivoRequest {
  return {
    limite: parseRecalculoLimite(form.limite),
    incluirManuales: form.incluirManuales,
  };
}

/* ---------------------------------------------------------------------------
 * Purga de conversaciones (`POST .../crm/purgar`, solo ADMIN)
 * ------------------------------------------------------------------------- */

/** Formulario de la purga. */
export interface PurgaFormState {
  /** Estado de lead a purgar (`''` = todos los estados). */
  estadoLead: EstadoLead | '';
  /** Días sin actividad, como texto (el `<input>` siempre es texto). */
  diasSinActividad: string;
  /** Tope de conversaciones a borrar en esta corrida, como texto. */
  limite: string;
}

export function emptyPurgaForm(): PurgaFormState {
  return {
    estadoLead: '',
    diasSinActividad: String(PURGA_DIAS_DEFAULT),
    limite: String(PURGA_LIMITE_DEFAULT),
  };
}

/**
 * Campos a los que `formErrorsFromApiError` puede atribuir un mensaje del backend. Son los del body
 * real (`estadoLead`, `diasSinActividad`, `limite`, `confirmar`), para que un 400 del schema caiga en
 * el campo que el usuario tiene delante.
 */
export const PURGA_FORM_FIELDS = [
  'estadoLead',
  'diasSinActividad',
  'limite',
  'confirmar',
] as const;

/**
 * Días de inactividad, ya como número dentro de lo que acepta el backend (1-3650).
 *
 * Un texto ilegible cae al default (90): la validación del diálogo lo marca ANTES, así que llegar acá
 * con basura significa que el admin no tocó el campo, y 90 días es el valor con el que se abrió.
 */
export function parsePurgaDias(value: string): number {
  return parseEnteroEnRango(value, PURGA_DIAS_DEFAULT, 1, PURGA_DIAS_MAX);
}

/** Tope de la corrida, ya como número dentro de lo que acepta el backend (1-5000). */
export function parsePurgaLimite(value: string): number {
  return parseEnteroEnRango(value, PURGA_LIMITE_DEFAULT, 1, PURGA_LIMITE_MAX);
}

/** Texto → entero dentro de `[minimo, maximo]`, con el default si no es un número usable. */
function parseEnteroEnRango(value: string, porDefecto: number, minimo: number, maximo: number): number {
  const texto = value.trim();
  if (texto.length === 0) return porDefecto;

  const parsed = Number(texto.replace(',', '.'));
  if (!Number.isFinite(parsed)) return porDefecto;

  return Math.min(Math.max(Math.trunc(parsed), minimo), maximo);
}

/**
 * Body del `POST .../crm/purgar`.
 *
 * `confirmar` lo decide el diálogo: la SIMULACIÓN (`false`, el paso obligatorio antes de borrar) y la
 * corrida real comparten el mismo body, así que lo que se vio en el conteo previo es exactamente lo
 * que se borra.
 */
export function purgaLeadsPayload(form: PurgaFormState, confirmar: boolean): PurgaLeadsRequest {
  return {
    ...(form.estadoLead ? { estadoLead: form.estadoLead } : {}),
    diasSinActividad: parsePurgaDias(form.diasSinActividad),
    limite: parsePurgaLimite(form.limite),
    confirmar,
  };
}
